import { useState, useRef, useEffect } from "react";
import { Upload, X, Image as ImageIcon } from "lucide-react";
import { useLocale } from "../../context/LanguageContext";

const ALLOWED_TYPES = ["image/jpeg", "image/png", "image/webp"];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5 MB

interface ImageUploaderProps {
  currentImageUrl?: string;
  onFileSelect: (file: File) => void;
  onRemove: () => void;
  error?: string | null;
  uploading?: boolean;
  /** Label above the upload area. Defaults to the localized "Cover Image". */
  label?: string;
  /** Hint text shown when no image is uploaded. Set null to hide. */
  fallbackHint?: string | null;
}

export function ImageUploader({
  currentImageUrl,
  onFileSelect,
  onRemove,
  error,
  uploading,
  label,
  fallbackHint,
}: ImageUploaderProps) {
  const { t } = useLocale();
  const resolvedLabel = label ?? t("adminUI.imageUploader.coverImage");
  const resolvedHint =
    fallbackHint === undefined ? t("adminUI.imageUploader.fallbackHint") : fallbackHint;
  const [preview, setPreview] = useState<string | null>(currentImageUrl ?? null);
  const [validationError, setValidationError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Sync preview when the external URL changes (e.g. org switch)
  useEffect(() => {
    setPreview(currentImageUrl ?? null);
    setValidationError(null);
  }, [currentImageUrl]);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setValidationError(null);

    if (!ALLOWED_TYPES.includes(file.type)) {
      setValidationError(t("adminUI.imageUploader.errorFileType"));
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setValidationError(t("adminUI.imageUploader.errorFileSize"));
      return;
    }

    setPreview(URL.createObjectURL(file));
    onFileSelect(file);

    // Reset input so the same file can be re-selected
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleRemove = () => {
    setPreview(null);
    setValidationError(null);
    onRemove();
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const displayError = validationError ?? error;

  return (
    <div>
      <label className="mb-1.5 block text-sm font-medium text-foreground">
        {resolvedLabel}
      </label>

      {preview ? (
        <div className="flex items-center gap-4">
          <div className="relative h-20 w-20 overflow-hidden rounded-lg border border-border">
            <img
              src={preview}
              alt={t("adminUI.imageUploader.previewAlt")}
              className="h-full w-full object-cover"
            />
            {uploading && (
              <div className="absolute inset-0 flex items-center justify-center bg-background/60">
                <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              </div>
            )}
          </div>
          <button
            type="button"
            onClick={handleRemove}
            className="flex items-center gap-1 text-sm text-destructive hover:text-destructive/80"
          >
            <X className="h-3.5 w-3.5" />
            {t("adminUI.imageUploader.remove")}
          </button>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="flex h-20 w-full items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/50 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:bg-muted"
        >
          {uploading ? (
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-primary border-t-transparent" />
          ) : (
            <>
              <Upload className="h-4 w-4" />
              <span>
                {t("adminUI.imageUploader.uploadPrompt")}{" "}
                <span className="text-xs">{t("adminUI.imageUploader.uploadConstraints")}</span>
              </span>
            </>
          )}
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp"
        onChange={handleFileChange}
        className="hidden"
      />

      {!preview && !uploading && resolvedHint && (
        <p className="mt-1 flex items-center gap-1 text-[11px] text-muted-foreground">
          <ImageIcon className="h-3 w-3" />
          {resolvedHint}
        </p>
      )}

      {displayError && (
        <p className="mt-1 text-xs text-destructive">{displayError}</p>
      )}
    </div>
  );
}
